import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Box, Card, CardContent, Link, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { compact, niceScale } from './reportsFormat';

// Primeras cuatro posiciones de la paleta categórica validada (azul, naranja, aqua, amarillo; orden fijo, nunca cíclico).
// Con más de cuatro series la pantalla que las arma agrupa el resto en 'Otros'.
export const SERIES_COLORS = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100'];
const INK = { primary: '#0b0b0b', secondary: '#52514e', muted: '#898781', grid: '#e1e0d9', baseline: '#c3c2b7', surface: '#fcfcfb' };
const MARGIN = { top: 12, right: 12, bottom: 28, left: 64 };
const BAR_MAX = 24;
const RADIUS = 4;

/** Ancho disponible del contenedor; el SVG se dibuja a tamaño real para que el texto no se deforme. */
function useWidth() {
  const ref = useRef(null);
  const [width, setWidth] = useState(600);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    setWidth(el.clientWidth || 600);
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => setWidth(el.clientWidth || 600));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return [ref, width];
}

/** Muestra una etiqueta de cada k para que las del eje X nunca se pisen. */
const visibleEvery = (n, width, labelPx = 64) => Math.max(1, Math.ceil((n * labelPx) / Math.max(width - MARGIN.left - MARGIN.right, 1)));

/** Barra con el extremo superior redondeado y la base recta, anclada a la línea base. */
const barPath = (x, y, w, h) => {
  const r = Math.min(RADIUS, w / 2, h);
  return `M${x},${y + h} V${y + r} Q${x},${y} ${x + r},${y} H${x + w - r} Q${x + w},${y} ${x + w},${y + r} V${y + h} Z`;
};

function Tooltip({ left, top, title, rows, containerWidth }) {
  const flip = left > containerWidth * 0.6;
  return (
    <Box
      role="status"
      sx={{
        position: 'absolute', left, top, pointerEvents: 'none', zIndex: 2,
        transform: `translate(${flip ? 'calc(-100% - 12px)' : '12px'}, 0)`,
        bgcolor: INK.surface, border: `1px solid ${INK.baseline}`, borderRadius: 1, boxShadow: 2, px: 1.25, py: 0.75, minWidth: 130,
      }}
    >
      <Typography variant="caption" sx={{ color: INK.secondary, display: 'block' }}>{title}</Typography>
      {rows.map((row) => (
        <Stack key={row.name} direction="row" spacing={0.75} alignItems="center">
          {row.color && <Box sx={{ width: 10, height: 2, bgcolor: row.color, borderRadius: 1 }} />}
          <Typography variant="body2" sx={{ color: INK.primary, fontWeight: 600 }}>{row.value}</Typography>
          <Typography variant="caption" sx={{ color: INK.secondary }}>{row.name}</Typography>
        </Stack>
      ))}
    </Box>
  );
}

function YAxis({ ticks, scaleY, width, format }) {
  return (
    <g>
      {ticks.map((t) => (
        <g key={t}>
          <line x1={MARGIN.left} x2={width - MARGIN.right} y1={scaleY(t)} y2={scaleY(t)} stroke={t === 0 ? INK.baseline : INK.grid} strokeWidth={1} />
          <text x={MARGIN.left - 8} y={scaleY(t) + 4} textAnchor="end" fontSize={11} fill={INK.muted}>{format(t)}</text>
        </g>
      ))}
    </g>
  );
}

/**
 * Columnas de una sola serie. data: [{ label, value, detail? }]. Cada columna es su propio blanco de hover/foco;
 * el valor exacto está en el tooltip y en la vista de tabla de la tarjeta.
 */
export function ColumnChart({ data, color = SERIES_COLORS[0], format = compact, seriesName = 'Valor', height = 220 }) {
  const [ref, width] = useWidth();
  const [hover, setHover] = useState(null);
  const innerW = Math.max(width - MARGIN.left - MARGIN.right, 10);
  const innerH = height - MARGIN.top - MARGIN.bottom;
  const { max, ticks } = niceScale(Math.max(0, ...data.map((d) => Number(d.value) || 0)));
  const scaleY = (v) => MARGIN.top + innerH - (v / max) * innerH;
  const slot = data.length ? innerW / data.length : innerW;
  const barW = Math.min(BAR_MAX, Math.max(2, slot - 2));
  const every = visibleEvery(data.length, width);

  return (
    <Box ref={ref} sx={{ position: 'relative', width: '100%' }} onPointerLeave={() => setHover(null)}>
      <svg width={width} height={height} role="img" aria-label={`Gráfico de columnas: ${seriesName}`}>
        <YAxis ticks={ticks} scaleY={scaleY} width={width} format={format} />
        {data.map((d, i) => {
          const value = Number(d.value) || 0;
          const x = MARGIN.left + slot * i + (slot - barW) / 2;
          const h = (value / max) * innerH;
          const active = hover?.index === i;
          return (
            <g key={`${d.label}-${i}`}>
              {h > 0 && <path d={barPath(x, MARGIN.top + innerH - h, barW, h)} fill={color} opacity={active ? 1 : 0.88} />}
              {i % every === 0 && (
                <text x={MARGIN.left + slot * i + slot / 2} y={height - 8} textAnchor="middle" fontSize={11} fill={INK.muted}>{d.label}</text>
              )}
              <rect
                x={MARGIN.left + slot * i} y={MARGIN.top} width={slot} height={innerH} fill="transparent" tabIndex={0}
                aria-label={`${d.label}: ${format(value)}`}
                onPointerMove={() => setHover({ index: i, left: MARGIN.left + slot * i + slot / 2, top: scaleY(value) })}
                onFocus={() => setHover({ index: i, left: MARGIN.left + slot * i + slot / 2, top: scaleY(value) })}
                onBlur={() => setHover(null)}
                style={{ outline: 'none' }}
              />
            </g>
          );
        })}
      </svg>
      {hover && (
        <Tooltip
          left={hover.left} top={Math.max(hover.top - 8, 0)} containerWidth={width} title={data[hover.index].label}
          rows={[{ name: data[hover.index].detail || seriesName, value: format(Number(data[hover.index].value) || 0), color }]}
        />
      )}
    </Box>
  );
}

/**
 * Líneas de una escala común (nunca doble eje). series: [{ name, color, values }], labels: una por posición.
 * La guía vertical sigue al puntero, se ancla al punto más cercano y el tooltip lista todas las series.
 */
export function LineChart({ labels, series, format = compact, height = 220 }) {
  const [ref, width] = useWidth();
  const [hover, setHover] = useState(null);
  const innerW = Math.max(width - MARGIN.left - MARGIN.right, 10);
  const innerH = height - MARGIN.top - MARGIN.bottom;
  const maxValue = Math.max(0, ...series.flatMap((s) => s.values.map((v) => Number(v) || 0)));
  const { max, ticks } = niceScale(maxValue);
  const scaleY = (v) => MARGIN.top + innerH - (v / max) * innerH;
  const scaleX = (i) => MARGIN.left + (labels.length <= 1 ? innerW / 2 : (innerW * i) / (labels.length - 1));
  const every = visibleEvery(labels.length, width);

  const locate = (event) => {
    const box = event.currentTarget.getBoundingClientRect();
    const x = event.clientX - box.left;
    const index = labels.length <= 1 ? 0 : Math.round(((x - MARGIN.left) / innerW) * (labels.length - 1));
    setHover(Math.min(Math.max(index, 0), labels.length - 1));
  };

  return (
    <Box ref={ref} sx={{ position: 'relative', width: '100%' }} onPointerLeave={() => setHover(null)}>
      <svg width={width} height={height} role="img" aria-label={`Gráfico de líneas: ${series.map((s) => s.name).join(', ')}`}>
        <YAxis ticks={ticks} scaleY={scaleY} width={width} format={format} />
        {labels.map((label, i) => i % every === 0 && (
          <text key={`${label}-${i}`} x={scaleX(i)} y={height - 8} textAnchor="middle" fontSize={11} fill={INK.muted}>{label}</text>
        ))}
        {hover !== null && <line x1={scaleX(hover)} x2={scaleX(hover)} y1={MARGIN.top} y2={MARGIN.top + innerH} stroke={INK.baseline} strokeWidth={1} />}
        {series.map((s) => {
          const points = s.values.map((v, i) => `${scaleX(i)},${scaleY(Number(v) || 0)}`).join(' ');
          const last = s.values.length - 1;
          return (
            <g key={s.name}>
              {s.values.length > 1 && <polyline points={points} fill="none" stroke={s.color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />}
              {last >= 0 && <circle cx={scaleX(last)} cy={scaleY(Number(s.values[last]) || 0)} r={4} fill={s.color} stroke={INK.surface} strokeWidth={2} />}
              {hover !== null && <circle cx={scaleX(hover)} cy={scaleY(Number(s.values[hover]) || 0)} r={4} fill={s.color} stroke={INK.surface} strokeWidth={2} />}
            </g>
          );
        })}
        <rect
          x={MARGIN.left} y={MARGIN.top} width={innerW} height={innerH} fill="transparent" tabIndex={0}
          onPointerMove={locate} onPointerDown={locate} onBlur={() => setHover(null)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowRight') setHover((h) => Math.min((h ?? -1) + 1, labels.length - 1));
            if (e.key === 'ArrowLeft') setHover((h) => Math.max((h ?? labels.length) - 1, 0));
          }}
          style={{ outline: 'none' }}
        />
      </svg>
      {hover !== null && (
        <Tooltip
          left={scaleX(hover)} top={MARGIN.top} containerWidth={width} title={labels[hover]}
          rows={series.map((s) => ({ name: s.name, value: format(Number(s.values[hover]) || 0), color: s.color }))}
        />
      )}
    </Box>
  );
}

/** Leyenda con la llave de cada serie: obligatoria con dos o más series, para que la identidad no dependa solo del color. */
export function Legend({ items }) {
  return (
    <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
      {items.map((item) => (
        <Stack key={item.name} direction="row" spacing={0.75} alignItems="center">
          <Box sx={{ width: 14, height: 3, bgcolor: item.color, borderRadius: 1 }} />
          <Typography variant="caption" sx={{ color: INK.secondary }}>{item.name}</Typography>
        </Stack>
      ))}
    </Stack>
  );
}

/** Tarjeta de un gráfico con título, subtítulo y alternancia gráfico / tabla (la tabla es la vía accesible a cada valor). */
export function ChartCard({ title, subtitle, legend, table, children, empty }) {
  const [showTable, setShowTable] = useState(false);
  useEffect(() => { if (empty) setShowTable(false); }, [empty]);
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{title}</Typography>
            {subtitle && <Typography variant="caption" sx={{ color: INK.secondary }}>{subtitle}</Typography>}
          </Box>
          {table && !empty && (
            <Link component="button" type="button" variant="caption" underline="hover" onClick={() => setShowTable((v) => !v)}>
              {showTable ? 'Ver gráfico' : 'Ver tabla'}
            </Link>
          )}
        </Stack>
        <Box sx={{ mt: 1.5 }}>
          {empty ? (
            <Typography variant="body2" sx={{ color: INK.secondary, py: 4, textAlign: 'center' }}>{empty}</Typography>
          ) : showTable ? (
            <Box sx={{ maxHeight: 260, overflow: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>{table.columns.map((c) => <TableCell key={c} align={c === table.columns[0] ? 'left' : 'right'}>{c}</TableCell>)}</TableRow>
                </TableHead>
                <TableBody>
                  {table.rows.map((row, i) => (
                    <TableRow key={i}>{row.map((cell, j) => <TableCell key={j} align={j === 0 ? 'left' : 'right'}>{cell}</TableCell>)}</TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          ) : (
            <>
              {legend}
              {children}
            </>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}

/** Tarjeta de un número: etiqueta, valor y una línea de contexto. */
export function StatTile({ label, value, hint, tone }) {
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="caption" sx={{ color: INK.secondary }}>{label}</Typography>
        <Typography sx={{ fontSize: 26, fontWeight: 600, lineHeight: 1.2, color: tone || INK.primary }}>{value}</Typography>
        {hint && <Typography variant="caption" sx={{ color: INK.secondary }}>{hint}</Typography>}
      </CardContent>
    </Card>
  );
}
